// Auto Research panel (Phase 5A → 5C).
//
// Mounted inside TestDetailView. Three UI states:
//   - Idle (no active session): start controls + button.
//   - Running (active session): live status badge + best run + Stop.
//   - Terminal (completed / stopped / failed): final summary + Dismiss.
//
// Polling: the panel polls the session endpoint every 2 s while a
// session is in 'running' state. The parent's own /api/tests/{id}
// poll (also 2 s) refreshes the Run rail as members land. The
// /events endpoint is polled alongside the session (since_id cursor)
// to surface driver-side progress; rows are deduped by event.id
// (mergeEventsById) to survive overlapping requests.
//
// Termination semantics by mode:
//   - Deterministic + Objective (click_sweep generator): target-met
//     cancellation is poll-driven. Closing the panel before the
//     2 s tick lands keeps pre-enqueued members running to
//     completion.
//   - Claude (Phase 5C): the backend AutoResearchDriver runs the
//     objective recompute on every tick AND on every JobRunner
//     completion-hook firing. Termination (target met, max_runs
//     reached, stop requested) is driver-driven and happens with
//     or without an attached client. The panel just reflects the
//     state — closing it does not affect termination.
import { useCallback, useEffect, useRef, useState } from "react";

import type { Run, Test } from "../api/tests";
import {
  startAutoResearch,
  getAutoResearchSession,
  stopAutoResearch,
  listAutoResearchSessions,
  postAutoResearchSteering,
  type AutoResearchSession,
} from "../api/auto_research";
import {
  fetchObjectives, objectiveContexts, type KpiObjective,
} from "../api/kpis";
import {
  fetchAutoResearchEvents,
  summarizeAutoResearchEvent,
  reconcilePredictedDeltas,
  type AutoResearchEvent,
  type PredictionReconRow,
} from "../api/auto_research";
import { CONFIG_CHANGED_EVENT, fetchConfig } from "../api/config";

type Mode = "deterministic" | "objective" | "claude";

/** Indexed by run_id — Claude's per-proposal context recovered from
 *  the run_enqueued event payload. The driver writes
 *  predicted_kpi_deltas + reasoning_summary into the event log only;
 *  the test_runs row never carries them (gate #3). */
type ClaudeProposalContext = {
  predicted_kpi_deltas?: Record<string, number> | null;
  reasoning_summary?: string | null;
  proposal_index?: number;
};

/** Append events by id, deduping any overlaps. Overlapping polls can
 *  in principle return the same event row twice (e.g. a slow first
 *  poll completes after a fresh one already advanced the cursor), and
 *  the panel renders the log by event.id so a duplicate would surface
 *  as two visually-identical rows. Cheap O(n+m) merge keyed on id. */
function mergeEventsById(
  prev: AutoResearchEvent[],
  incoming: AutoResearchEvent[],
): AutoResearchEvent[] {
  if (incoming.length === 0) return prev;
  const seen = new Set<number>();
  const out: AutoResearchEvent[] = [];
  for (const e of prev) {
    if (!seen.has(e.id)) {
      seen.add(e.id);
      out.push(e);
    }
  }
  for (const e of incoming) {
    if (!seen.has(e.id)) {
      seen.add(e.id);
      out.push(e);
    }
  }
  return out;
}

const POLL_INTERVAL_MS = 2000;

export type AutoResearchPanelProps = {
  test: Test;
  onRunCreated: () => void;
};

function isTerminal(state: AutoResearchSession["state"]): boolean {
  return state !== "running";
}

function bestLabel(sess: AutoResearchSession, runs: Run[]): string {
  if (sess.best_run_id === null || sess.best_score === null) return "—";
  const run = runs.find((r) => r.id === sess.best_run_id);
  const ord = run ? `R${run.run_number}` : `run #${sess.best_run_id}`;
  const score = sess.best_score.toFixed(3);
  // Pick the score label by mode. Objective sessions persist
  // target_kpi=null because the score is the objective composite
  // (kpi_evaluators._compute_score), NOT a single KPI value; rendering
  // 'mean_r2_dyn' there would lie about what the number means. Prefer
  // the objective_id label; fall back to the legacy target_kpi only
  // when no objective is set.
  const label = sess.objective_id !== null
    ? `${sess.objective_id} (objective score)`
    : (sess.target_kpi ?? "mean_r2_dyn");
  return `${ord} · ${label} ${score}`;
}

function stateTone(state: AutoResearchSession["state"]): string {
  switch (state) {
    case "running":   return "#7ed47e";
    case "completed": return "#7ed47e";
    case "stopped":   return "#e4c47a";
    case "failed":    return "#ff9a9a";
  }
}

/** Signed, compact delta for the reconciliation table. Positive values
 *  carry an explicit '+' so a column reads directionally; null renders
 *  as a bare dash (missing-value marker). */
function formatDelta(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return "–";
  const s = v.toFixed(4).replace(/\.?0+$/, "");
  return v > 0 ? `+${s}` : s;
}

/** Muted chip tones (reused from the state badges) keyed by sign
 *  agreement; 'na' inherits the surrounding muted colour. */
function agreementColor(a: PredictionReconRow["agreement"]): string | undefined {
  if (a === "agree") return "#7ed47e";
  if (a === "disagree") return "#e4c47a";
  return undefined;
}

/** Pretty-print an untyped payload for the raw-JSON fallback disclosure,
 *  never throwing (wire JSON is not circular, but stay defensive). */
function safeStringifyPayload(payload: unknown): string {
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return "[unserializable payload]";
  }
}

export function AutoResearchPanel(
  { test, onRunCreated }: AutoResearchPanelProps,
) {
  const [session, setSession] = useState<AutoResearchSession | null>(null);
  // Default mirrors the API's max_runs default (12 since 2026-06-17 — the
  // budget-gated plateau / Claude's request_stop end flat sessions early).
  const [maxRuns, setMaxRuns] = useState(12);
  const [targetValue, setTargetValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<AutoResearchSession | null>(null);
  sessionRef.current = session;

  // Mid-study engineer steering (INC2-C). The message is posted to the
  // running session's event log; the driver folds every steering message
  // into each subsequent proposal turn as standing guidance. Send state is
  // kept separate from the Start/Stop `busy` flag so steering never blocks
  // (or is blocked by) a lifecycle action.
  const [steeringMessage, setSteeringMessage] = useState("");
  const [steeringBusy, setSteeringBusy] = useState(false);
  const [steeringError, setSteeringError] = useState<string | null>(null);

  // Objective-mode state. `mode` toggles between the legacy
  // deterministic flow (mean_r2_dyn target), the Phase 5B
  // objective-driven flow, and the Phase 5C Claude loop. Objectives
  // are fetched once on mount; a failure leaves the list empty and
  // both Objective + Claude modes disabled.
  // Track-replay Studies (2026-06-11 validity-guardrails build) score on
  // replay-context objectives (engineering-validity gates) — the legacy
  // deterministic mean_r2_dyn mode is structurally unscorable there (no
  // recorded damper truth) and the backend 422s it. Default such Studies
  // into Claude mode and filter the objective list by context.
  const isReplayStudy = Boolean(
    (test.kpi_targets as { track_replay?: { enabled?: boolean } } | null)
      ?.track_replay?.enabled,
  );
  const studyContext = isReplayStudy ? "replay" : "seven_post";

  const [mode, setMode] = useState<Mode>(
    isReplayStudy ? "claude" : "deterministic",
  );
  const [objectives, setObjectives] = useState<KpiObjective[]>([]);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState<string>("");
  const [objectiveTargetValue, setObjectiveTargetValue] = useState("");
  // T1c exploration knobs (Claude mode): blank window = backend default.
  const [plateauWindow, setPlateauWindow] = useState("");
  const [plateauDisabled, setPlateauDisabled] = useState(false);
  // Protocol mode (Claude only): structure the loop into design/screen/
  // exploit/verify. Sends protocol=true; the panel shows a phase chip +
  // the design's hypotheses while running.
  const [protocolMode, setProtocolMode] = useState(false);
  // T3 Pareto: ADDITIONAL objectives beyond the primary; ≥1 selected makes
  // the session multi-objective (objective_ids = [primary, ...these]).
  const [paretoIds, setParetoIds] = useState<string[]>([]);
  const [objectiveLoadError, setObjectiveLoadError] = useState(false);
  const [activeClaudeModel, setActiveClaudeModel] = useState<string | null>(
    null,
  );

  // Phase 5C event log. The driver writes Claude/run-lifecycle events
  // here; long-polled alongside the session refresh. Indexed by
  // run_id so a member row can render Claude's predicted deltas +
  // reasoning beside its actual KPI scores.
  const [events, setEvents] = useState<AutoResearchEvent[]>([]);
  const eventsCursorRef = useRef<number>(0);
  // Action-space disclosure (2026-07-02): the driver's first tick records
  // which levers the session actually has. all_click_dead = every corner
  // lacks PVP click coverage → the study degrades to the fv_scale overlay
  // only, on fallback damper curves — say so up front, not after N runs.
  const actionSpace = (() => {
    for (const ev of events) {
      if (ev.kind === "action_space") {
        return ev.payload as {
          all_click_dead?: boolean;
          levers?: string[];
        };
      }
    }
    return null;
  })();
  // Protocol mode (2026-07-12): the current phase (latest protocol_phase
  // event; "design" once protocol_enabled arrives and no phase yet) and the
  // design's hypotheses (from the protocol_design payload). Both derived from
  // the same event log the panel already polls, so no extra request.
  const protocolEnabled = events.some((ev) => ev.kind === "protocol_enabled");
  const protocolPhase = (() => {
    if (!protocolEnabled) return null;
    let phase = "design";
    for (const ev of events) {
      if (ev.kind === "protocol_phase") {
        const p = (ev.payload ?? {}) as Record<string, unknown>;
        if (typeof p.phase === "string") phase = p.phase;
      }
    }
    return phase;
  })();
  const protocolHypotheses = (() => {
    let hyps: string[] = [];
    for (const ev of events) {
      if (ev.kind === "protocol_design") {
        const p = (ev.payload ?? {}) as Record<string, unknown>;
        if (Array.isArray(p.hypotheses)) {
          hyps = (p.hypotheses as unknown[]).filter(
            (x): x is string => typeof x === "string",
          );
        }
      }
    }
    return hyps;
  })();
  const claudeContextByRunId: Record<number, ClaudeProposalContext> = {};
  for (const ev of events) {
    if (ev.kind === "run_enqueued") {
      const p = ev.payload as Record<string, unknown>;
      const runId = typeof p.run_id === "number" ? p.run_id : null;
      if (runId !== null) {
        claudeContextByRunId[runId] = {
          predicted_kpi_deltas: (p.predicted_kpi_deltas
            ?? null) as Record<string, number> | null,
          reasoning_summary: (p.reasoning_summary
            ?? null) as string | null,
          proposal_index: typeof p.proposal_index === "number"
            ? p.proposal_index
            : undefined,
        };
      }
    }
  }

  useEffect(() => {
    let cancelled = false;
    fetchObjectives()
      .then((list) => {
        if (cancelled) return;
        setObjectives(list);
        // Pre-select the first selectable (non-'unavailable') objective
        // FOR THIS STUDY'S CONTEXT so opening Objective/Claude mode
        // doesn't show an empty or unscoreable dropdown.
        const first = list.find(
          (o) =>
            o.trust_level !== "unavailable"
            && objectiveContexts(o).includes(studyContext),
        );
        if (first) setSelectedObjectiveId(first.id);
      })
      .catch(() => { if (!cancelled) setObjectiveLoadError(true); });
    return () => { cancelled = true; };
  }, [studyContext]);

  useEffect(() => {
    let cancelled = false;
    const loadClaudeModel = async () => {
      try {
        const cfg = await fetchConfig();
        if (!cancelled) setActiveClaudeModel(cfg.claude_model ?? null);
      } catch {
        if (!cancelled) setActiveClaudeModel(null);
      }
    };
    void loadClaudeModel();
    const onChange = () => { void loadClaudeModel(); };
    window.addEventListener(CONFIG_CHANGED_EVENT, onChange);
    return () => {
      cancelled = true;
      window.removeEventListener(CONFIG_CHANGED_EVENT, onChange);
    };
  }, []);

  // On mount: re-attach to an in-flight session if one exists. Silently
  // swallow listing errors so the Idle UI still renders even if the
  // backend list endpoint is briefly unavailable — the user can still
  // start a new session, which will surface its own error if anything
  // is wrong.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listAutoResearchSessions(test.id);
        const active = list.find((s) => s.state === "running");
        if (!cancelled && active) {
          const full = await getAutoResearchSession(active.id);
          if (!cancelled) setSession(full);
          // Phase 5C: also pull the event log on attach so a
          // reconnect-after-refresh sees the driver's history right
          // away (rather than only the next 2 s poll tick onward).
          // Independent try/catch — events are nice-to-have.
          try {
            const resp = await fetchAutoResearchEvents(active.id, 0);
            if (!cancelled && resp.events.length > 0) {
              eventsCursorRef.current = resp.next_since_id;
              setEvents(resp.events);
            }
          } catch {
            // Quiet: events are diagnostic, not critical.
          }
        }
      } catch {
        // Idle state is the safe default; don't block the panel on a
        // transient listing failure.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [test.id]);

  const refresh = useCallback(async () => {
    const current = sessionRef.current;
    if (!current) return;
    try {
      const next = await getAutoResearchSession(current.id);
      setSession(next);
      // A successful poll dismisses any stale steering-send error: the
      // session state is now authoritative, and if it went terminal the
      // steering row unmounts anyway.
      setSteeringError(null);
      // Refresh the parent Test so the Run rail picks up status
      // changes between the parent's own 2 s polls.
      onRunCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    // Phase 5C: incremental event poll. Independent try/catch so an
    // events-endpoint hiccup doesn't drop the session refresh.
    try {
      const resp = await fetchAutoResearchEvents(
        current.id, eventsCursorRef.current,
      );
      if (resp.events.length > 0) {
        eventsCursorRef.current = resp.next_since_id;
        setEvents((prev) => mergeEventsById(prev, resp.events));
      }
    } catch {
      // Events are nice-to-have, not critical. Quiet.
    }
  }, [onRunCreated]);

  // Poll while running.
  useEffect(() => {
    if (!session || isTerminal(session.state)) return;
    const handle = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [session, refresh]);

  // Target input is non-empty but doesn't parse as a finite number —
  // we block Start so the engineer doesn't accidentally launch an
  // untargeted session thinking they'd set a stopping criterion.
  const trimmedTarget = targetValue.trim();
  const targetParsed = trimmedTarget.length === 0
    ? null
    : Number(trimmedTarget);
  const targetInvalid =
    trimmedTarget.length > 0 && !Number.isFinite(targetParsed);

  // Only objectives scoreable on this Study's actuation mode are
  // selectable (replay objectives gate on engineering validity; 7-post
  // objectives on recorded-truth fidelity — neither scores on the other).
  const contextObjectives = objectives.filter(
    (o) =>
      o.trust_level !== "unavailable"
      && objectiveContexts(o).includes(studyContext),
  );
  const selectedObjective = contextObjectives.find(
    (o) => o.id === selectedObjectiveId,
  );
  const isConditionalReachability =
    isReplayStudy && selectedObjective?.replay_reachability === "conditional";

  // Objective-mode target: separate from the deterministic target
  // because the comparison semantic is different (objective score vs
  // mean_r2_dyn). Same numeric-validation gate.
  const objTrimmed = objectiveTargetValue.trim();
  const objTargetParsed = objTrimmed.length === 0
    ? null
    : Number(objTrimmed);
  const objTargetInvalid =
    objTrimmed.length > 0 && !Number.isFinite(objTargetParsed);

  // Both Objective AND Claude modes share the objective-side guards.
  const objectiveStartDisabled =
    (mode === "objective" || mode === "claude")
    && (!selectedObjective || objTargetInvalid);

  const onStart = useCallback(async () => {
    setError(null);
    if (mode === "deterministic") {
      if (targetInvalid) {
        setError(
          `target value ${JSON.stringify(trimmedTarget)} isn't numeric — `
          + "leave it blank for no target or enter a number like 0.85.",
        );
        return;
      }
    } else {
      // Both Objective and Claude modes need an objective + valid
      // optional target. Claude additionally sends generator='claude'.
      if (!selectedObjective) {
        setError(
          `Pick an objective before starting in ${
            mode === "claude" ? "Claude" : "Objective"
          } mode.`,
        );
        return;
      }
      if (objTargetInvalid) {
        setError(
          `objective_target_value ${JSON.stringify(objTrimmed)} isn't numeric — `
          + "leave blank for no target or enter a number.",
        );
        return;
      }
    }
    setBusy(true);
    try {
      const body: Parameters<typeof startAutoResearch>[1] = {
        max_runs: maxRuns,
      };
      if (mode === "deterministic") {
        if (targetParsed !== null && Number.isFinite(targetParsed)) {
          body.target_kpi = "mean_r2_dyn";
          body.target_direction = "max";
          body.target_value = targetParsed;
        }
      } else if (selectedObjective) {
        body.objective_id = selectedObjective.id;
        if (objTargetParsed !== null && Number.isFinite(objTargetParsed)) {
          body.objective_target_value = objTargetParsed;
        }
        if (mode === "claude") {
          body.generator = "claude";
          // Protocol mode: structure the loop into design/screen/exploit/verify.
          if (protocolMode) body.protocol = true;
          // T1c exploration knobs.
          const pw = Number(plateauWindow);
          if (plateauWindow.trim() !== "" && Number.isFinite(pw) && pw >= 1) {
            body.plateau_window = Math.floor(pw);
          }
          if (plateauDisabled) body.plateau_disabled = true;
          // T3 Pareto: primary + extra objectives → objective_ids (≥2).
          const extra = paretoIds.filter((id) => id !== selectedObjective.id);
          if (extra.length > 0) {
            body.objective_ids = [selectedObjective.id, ...extra];
            // A Pareto study tracks a front, not a single target.
            delete body.objective_target_value;
          }
        }
      }
      const s = await startAutoResearch(test.id, body);
      setSession(s);
      // Reset the events cursor for this fresh session so the first
      // poll picks up the driver's startup events.
      eventsCursorRef.current = 0;
      setEvents([]);
      onRunCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [
    mode, maxRuns,
    targetParsed, targetInvalid, trimmedTarget,
    selectedObjective, objTargetParsed, objTargetInvalid, objTrimmed,
    plateauWindow, plateauDisabled, protocolMode, paretoIds,
    test.id, onRunCreated,
  ]);

  const onStop = useCallback(async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const s = await stopAutoResearch(session.id);
      setSession(s);
      onRunCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [session, onRunCreated]);

  const onDismiss = useCallback(() => setSession(null), []);

  // Send a standing steering message to the running session. Reads the
  // session from the ref so the callback stays stable across polls. On
  // success the input clears; the message reappears in the event log on
  // the next poll (kind="engineer_steering"), so there is no optimistic
  // echo. The only failures reachable while the row is shown are 409 (the
  // session just went terminal) and 404 (it vanished) — both mean there
  // are no future turns to steer.
  const onSteer = useCallback(async () => {
    const current = sessionRef.current;
    if (!current) return;
    const msg = steeringMessage.trim();
    if (msg.length === 0) return;
    setSteeringBusy(true);
    setSteeringError(null);
    try {
      await postAutoResearchSteering(current.id, msg);
      setSteeringMessage("");
    } catch {
      setSteeringError(
        "Steering not sent: the session is no longer accepting guidance "
        + "(it may have just finished). This clears on the next poll.",
      );
    } finally {
      setSteeringBusy(false);
    }
  }, [steeringMessage]);

  // source_event is the 7-post requirement (the campaign recording +
  // gate identity). A REPLAY Study's actuation is the ingested lap —
  // own-setup replay Studies (kpi_targets.setup_file) legitimately have
  // no source_event and must not be blocked here (Studies #7/#8).
  const disabledReason = !test.source_event && !isReplayStudy
    ? "Test has no source_event — set one before running AutoResearch"
    : null;
  const claudeModelLabel = activeClaudeModel ?? "settings default";

  return (
    <div
      className="card auto-research-panel"
      data-testid="auto-research-panel"
      style={{ marginTop: "12px" }}
    >
      <h4 style={{ margin: "0 0 8px" }}>AutoResearch</h4>

      {session === null ? (
        <div
          style={{
            display: "flex", gap: "12px", alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <label style={{ fontSize: "12px" }}>
            mode
            <select
              className="input"
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
              style={{ marginLeft: "4px" }}
              data-testid="auto-research-mode"
              disabled={objectiveLoadError && mode === "deterministic"}
            >
              {/* Replay Studies have no recorded damper truth — the
                  legacy mean_r2_dyn target can never score there. */}
              <option value="deterministic" disabled={isReplayStudy}>
                Deterministic (mean_r2_dyn)
              </option>
              <option value="objective" disabled={contextObjectives.length === 0}>
                Objective
              </option>
              <option value="claude" disabled={contextObjectives.length === 0}>
                Claude
              </option>
            </select>
          </label>
          {mode === "claude" && (
            <span
              data-testid="auto-research-claude-model-badge"
              style={{
                fontSize: "11px",
                color: "var(--text-3)",
                padding: "2px 6px",
                border: "1px solid var(--line-2)",
                borderRadius: 4,
              }}
            >
              model: {claudeModelLabel}
            </span>
          )}
          <label style={{ fontSize: "12px" }}>
            max_runs
            <input
              className="input"
              type="number"
              min={1}
              max={250}
              value={maxRuns}
              onChange={(e) =>
                setMaxRuns(Math.max(1, Math.min(250, Number(e.target.value) || 4)))
              }
              style={{ marginLeft: "4px", width: "60px" }}
              data-testid="auto-research-max-runs"
            />
          </label>
          {mode === "deterministic" ? (
            <label style={{ fontSize: "12px" }}>
              target mean_r2_dyn ≥
              <input
                className="input"
                type="text"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder="(optional)"
                style={{
                  marginLeft: "4px",
                  width: "80px",
                  border: targetInvalid ? "1px solid #ff9a9a" : undefined,
                }}
                data-testid="auto-research-target-value"
                aria-invalid={targetInvalid}
              />
            </label>
          ) : (
            <>
              <label style={{ fontSize: "12px" }}>
                objective
                <select
                  className="input"
                  value={selectedObjectiveId}
                  onChange={(e) => setSelectedObjectiveId(e.target.value)}
                  style={{ marginLeft: "4px", minWidth: "220px" }}
                  data-testid="auto-research-objective-select"
                >
                  {contextObjectives
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.display_name}
                        {isReplayStudy
                          && o.replay_reachability === "conditional"
                          ? " · ⚠ weakly reachable"
                          : ""}
                      </option>
                    ))}
                </select>
              </label>
              {isConditionalReachability && (
                <span
                  data-testid="auto-research-reachability-warning"
                  style={{
                    fontSize: "11px",
                    color: "#e0c97a",
                    padding: "2px 6px",
                    border: "1px solid var(--line-2)",
                    borderRadius: 4,
                  }}
                  title={
                    "Probe evidence says the current damper levers "
                    + "(clicks + F-V scale) move this objective weakly on "
                    + "replay — the session may explore without much "
                    + "objective movement."
                  }
                >
                  ⚠ conditional reachability
                </span>
              )}
              <label style={{ fontSize: "12px" }}>
                target ({selectedObjective?.direction ?? "?"})
                <input
                  className="input"
                  type="text"
                  value={objectiveTargetValue}
                  onChange={(e) => setObjectiveTargetValue(e.target.value)}
                  placeholder="(optional)"
                  style={{
                    marginLeft: "4px",
                    width: "80px",
                    border: objTargetInvalid ? "1px solid #ff9a9a" : undefined,
                  }}
                  data-testid="auto-research-objective-target"
                  aria-invalid={objTargetInvalid}
                />
              </label>
              {mode === "claude" && (
                <>
                  <label
                    style={{ fontSize: "12px" }}
                    title={
                      "Consecutive non-improving feasible runs before the "
                      + "study auto-stops. Higher = explores longer. Blank = "
                      + "default."
                    }
                  >
                    explore window
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={64}
                      value={plateauWindow}
                      onChange={(e) => setPlateauWindow(e.target.value)}
                      placeholder="def"
                      style={{ marginLeft: "4px", width: "52px" }}
                      data-testid="auto-research-plateau-window"
                    />
                  </label>
                  <label
                    style={{ fontSize: "12px" }}
                    title={
                      "Never auto-stop on convergence — spend the whole "
                      + "max_runs budget exploring."
                    }
                  >
                    <input
                      type="checkbox"
                      checked={plateauDisabled}
                      onChange={(e) => setPlateauDisabled(e.target.checked)}
                      style={{ marginRight: "4px" }}
                      data-testid="auto-research-plateau-disabled"
                    />
                    no auto-stop
                  </label>
                  <label
                    style={{ fontSize: "12px" }}
                    title={
                      "Structure the study into a design phase (Claude plans "
                      + "the experiment), a deterministic screen of the "
                      + "reachable space, an exploit phase, and a verify "
                      + "replication of the winner."
                    }
                  >
                    <input
                      type="checkbox"
                      checked={protocolMode}
                      onChange={(e) => setProtocolMode(e.target.checked)}
                      style={{ marginRight: "4px" }}
                      data-testid="auto-research-protocol"
                    />
                    Protocol study (design, screen, exploit, verify)
                  </label>
                  {contextObjectives.length > 1 && (
                    <details
                      style={{ fontSize: "12px" }}
                      data-testid="auto-research-pareto"
                    >
                      <summary style={{ cursor: "pointer" }}>
                        + Pareto objectives
                        {paretoIds.filter((id) => id !== selectedObjectiveId)
                          .length > 0
                          ? ` (${
                              paretoIds.filter(
                                (id) => id !== selectedObjectiveId,
                              ).length
                            })`
                          : ""}
                      </summary>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          marginTop: "4px",
                          maxHeight: "140px",
                          overflowY: "auto",
                        }}
                      >
                        {contextObjectives
                          .filter((o) => o.id !== selectedObjectiveId)
                          .map((o) => (
                            <label key={o.id} style={{ fontSize: "11px" }}>
                              <input
                                type="checkbox"
                                checked={paretoIds.includes(o.id)}
                                onChange={(e) =>
                                  setParetoIds((prev) =>
                                    e.target.checked
                                      ? [...prev, o.id]
                                      : prev.filter((x) => x !== o.id),
                                  )
                                }
                                style={{ marginRight: "4px" }}
                              />
                              {o.display_name}
                            </label>
                          ))}
                      </div>
                    </details>
                  )}
                </>
              )}
            </>
          )}
          <button
            type="button"
            className="btn primary"
            onClick={onStart}
            disabled={
              busy
              || disabledReason !== null
              || (mode === "deterministic" && targetInvalid)
              || objectiveStartDisabled
            }
            title={
              disabledReason
              ?? (mode === "deterministic" && targetInvalid
                ? "target value isn't numeric — blank for no target or e.g. 0.85"
                : "Generate damper-setting proposals as Runs")
            }
            data-testid="auto-research-start-btn"
          >
            {busy ? "Starting…" : "Start AutoResearch"}
          </button>
          {mode === "deterministic" && targetInvalid && (
            <span
              data-testid="auto-research-target-invalid"
              style={{ color: "#ff9a9a", fontSize: "11px" }}
            >
              non-numeric — clear it or use a number like 0.85
            </span>
          )}
          {(mode === "objective" || mode === "claude")
            && objTargetInvalid && (
              <span
                data-testid="auto-research-objective-target-invalid"
                style={{ color: "#ff9a9a", fontSize: "11px" }}
              >
                non-numeric — clear it or use a number
              </span>
            )}
          {objectiveLoadError && (
            <span
              className="muted"
              style={{ fontSize: "11px", flexBasis: "100%" }}
            >
              Objective list unavailable — Objective + Claude modes
              are disabled until the /api/kpis/objectives endpoint
              recovers.
            </span>
          )}
          {(mode === "deterministic" || mode === "objective") && (
            <span
              className="muted"
              style={{ fontSize: "11px", flexBasis: "100%" }}
              data-testid="auto-research-poll-driven-note"
            >
              UI-driven: target cancellation only fires while this panel
              is open and polling.
            </span>
          )}
          {mode === "claude" && (
            <span
              className="muted"
              style={{ fontSize: "11px", flexBasis: "100%" }}
              data-testid="auto-research-driver-driven-note"
            >
              Driver-driven: the Claude loop terminates from the
              backend driver on its own cadence (target met,
              max_runs reached, or stop). Closing the panel does
              not affect termination.
            </span>
          )}
        </div>
      ) : (
        <div>
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span
              data-testid="auto-research-state"
              style={{ fontWeight: 600, color: stateTone(session.state) }}
            >
              {session.state === "running" && (
                <span className="row-activity-dot" aria-hidden="true" />
              )}
              {session.state}
            </span>
            <span className="muted" style={{ fontSize: "12px" }}>
              {session.runs_terminal} / {session.runs_total} runs complete
            </span>
            <span className="muted" style={{ fontSize: "12px" }}>
              best: <strong>{bestLabel(session, test.runs)}</strong>
            </span>
            {session.state === "running" ? (
              <button
                type="button"
                className="btn"
                onClick={onStop}
                disabled={busy}
                data-testid="auto-research-stop-btn"
              >
                {busy ? "Stopping…" : "Stop"}
              </button>
            ) : (
              <button
                type="button"
                className="btn"
                onClick={onDismiss}
                data-testid="auto-research-dismiss-btn"
              >
                Dismiss
              </button>
            )}
          </div>
          {session.state === "running" && (
            <>
              <div
                data-testid="auto-research-steering"
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  marginTop: "8px",
                }}
              >
                <input
                  className="input"
                  type="text"
                  value={steeringMessage}
                  onChange={(e) => setSteeringMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void onSteer();
                    }
                  }}
                  placeholder="Steer the agent: pin a lever, exclude a region, redirect focus"
                  maxLength={2000}
                  disabled={steeringBusy}
                  style={{ flex: 1, minWidth: "220px", fontSize: "12px" }}
                  data-testid="auto-research-steering-input"
                  aria-label="Engineer steering message"
                />
                <button
                  type="button"
                  className="btn"
                  onClick={onSteer}
                  disabled={steeringBusy || steeringMessage.trim().length === 0}
                  title="Send standing guidance the agent applies to every subsequent proposal"
                  data-testid="auto-research-steering-send-btn"
                >
                  {steeringBusy ? "Sending…" : "Send"}
                </button>
              </div>
              {steeringError && (
                <p
                  className="muted"
                  data-testid="auto-research-steering-error"
                  style={{ fontSize: "11px", marginTop: "4px" }}
                >
                  {steeringError}
                </p>
              )}
            </>
          )}
          {actionSpace?.all_click_dead && (
            <p
              data-testid="auto-research-action-space-warning"
              style={{ color: "#e8c46a", fontSize: "12px", marginTop: "4px" }}
            >
              ⚠ Clicks unavailable on every corner (no PVP coverage for this
              setup&apos;s damper codes) — this study explores the
              experimental fv_scale overlay only, on fallback damper curves.
            </p>
          )}
          {/* Protocol study (2026-07-12): a phase chip + the design's
              hypotheses, derived from the event log the panel already polls. */}
          {protocolPhase !== null && (
            <div style={{ marginTop: "6px" }}>
              <span
                data-testid="auto-research-protocol-phase"
                style={{
                  fontSize: "11px",
                  color: "var(--text-2)",
                  padding: "2px 8px",
                  border: "1px solid var(--line-2)",
                  borderRadius: 999,
                }}
                title="Protocol study phase: design, screen, exploit, verify."
              >
                protocol: {protocolPhase}
              </span>
              {protocolHypotheses.length > 0 && (
                <details
                  data-testid="auto-research-protocol-hypotheses"
                  style={{ fontSize: "11px", marginTop: "4px" }}
                >
                  <summary className="muted" style={{ cursor: "pointer" }}>
                    hypotheses ({protocolHypotheses.length})
                  </summary>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                    {protocolHypotheses.map((h, i) => (
                      <li key={i}>{h}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          {session.error && (
            <p
              data-testid="auto-research-session-error"
              style={{ color: "#ff9a9a", fontSize: "12px", marginTop: "4px" }}
            >
              {session.error}
            </p>
          )}
          {/* Phase 5C — Claude session: surface cost + token usage
              for the engineer + the per-member predicted-vs-actual
              context Claude provided. */}
          {session.generator === "claude" && (
            <div data-testid="auto-research-claude-summary" style={{ marginTop: 8 }}>
              <div className="muted" style={{ fontSize: "11px" }}>
                claude {session.model ?? claudeModelLabel}
                {" · "}
                proposals: {session.current_proposal_index}
                {" · "}
                tokens in/out:{" "}
                {session.total_input_tokens.toLocaleString()}
                {" / "}
                {session.total_output_tokens.toLocaleString()}
                {session.total_cache_read_tokens > 0 && (
                  <>
                    {" · cache read: "}
                    {session.total_cache_read_tokens.toLocaleString()}
                  </>
                )}
                {session.estimated_cost_usd !== null && (
                  <>
                    {" · est cost: $"}
                    {session.estimated_cost_usd.toFixed(4)}
                  </>
                )}
              </div>
              {session.member_runs.length > 0 && (
                <div
                  data-testid="auto-research-claude-member-proposals"
                  style={{ marginTop: 6 }}
                >
                  {session.member_runs.map((m) => {
                    const ctx = claudeContextByRunId[m.run_id];
                    if (!ctx) return null;
                    // Actual KPI deltas are NOT in the session payload's
                    // member_runs (run_id/proposal_index/status only). They
                    // live on the run rail (test.runs[].kpi_scores), joined
                    // by run id against the session baseline (R0): the same
                    // join bestLabel uses. Predicted deltas are stated vs the
                    // session baseline, so actual delta = member KPI less the
                    // baseline KPI.
                    const memberRun = test.runs.find((r) => r.id === m.run_id);
                    const baselineRun =
                      (memberRun && memberRun.baseline_run_id !== null
                        ? test.runs.find(
                            (r) => r.id === memberRun.baseline_run_id,
                          )
                        : undefined)
                      ?? test.runs.find((r) => r.run_number === 0)
                      ?? test.runs.find((r) => r.source === "baseline");
                    const memberKpis = memberRun?.kpi_scores ?? null;
                    const baselineKpis = baselineRun?.kpi_scores ?? null;
                    const reconRows = reconcilePredictedDeltas(
                      ctx.predicted_kpi_deltas, memberKpis, baselineKpis,
                    );
                    // No usable actuals: member not complete, or the run
                    // carries no kpi_scores yet (rejected / in-flight / failed).
                    const noActual =
                      m.status !== "complete" || memberKpis === null;
                    return (
                      <details
                        key={m.run_id}
                        data-testid={`claude-proposal-${m.run_id}`}
                        style={{ fontSize: "12px", marginTop: 4 }}
                      >
                        <summary>
                          proposal #{ctx.proposal_index ?? m.proposal_index}{" "}
                          (run {m.run_id}, {m.status}) — Claude's prediction
                        </summary>
                        {ctx.reasoning_summary && (
                          <div style={{ marginTop: 4, paddingLeft: 12 }}>
                            <em>reasoning:</em> {ctx.reasoning_summary}
                          </div>
                        )}
                        {ctx.predicted_kpi_deltas && reconRows.length > 0 && (
                          <div
                            style={{ marginTop: 4, paddingLeft: 12 }}
                            data-testid={`claude-predicted-${m.run_id}`}
                          >
                            <em>
                              predicted vs actual Δ (predictions logged only,
                              not scored):
                            </em>
                            <table
                              data-testid={`claude-reconciliation-${m.run_id}`}
                              style={{
                                borderCollapse: "collapse",
                                margin: "2px 0 0",
                                fontSize: "11px",
                              }}
                            >
                              <thead>
                                <tr style={{ color: "var(--text-3)" }}>
                                  <th
                                    style={{
                                      padding: "1px 8px 1px 0",
                                      textAlign: "left",
                                    }}
                                  >
                                    KPI
                                  </th>
                                  <th
                                    style={{
                                      padding: "1px 8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    predicted Δ
                                  </th>
                                  <th
                                    style={{
                                      padding: "1px 8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    actual Δ
                                  </th>
                                  <th
                                    style={{
                                      padding: "1px 0 1px 8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    signed error
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {reconRows.map((row) => (
                                  <tr
                                    key={row.kpi}
                                    data-testid={
                                      `recon-row-${m.run_id}-${row.kpi}`
                                    }
                                    data-agreement={row.agreement}
                                  >
                                    <td style={{ padding: "1px 8px 1px 0" }}>
                                      <code>{row.kpi}</code>
                                    </td>
                                    <td
                                      style={{
                                        padding: "1px 8px",
                                        textAlign: "right",
                                      }}
                                    >
                                      {formatDelta(row.predicted)}
                                    </td>
                                    <td
                                      style={{
                                        padding: "1px 8px",
                                        textAlign: "right",
                                        color: agreementColor(row.agreement),
                                      }}
                                    >
                                      {noActual
                                        ? "no actual (rejected)"
                                        : formatDelta(row.actual)}
                                    </td>
                                    <td
                                      style={{
                                        padding: "1px 0 1px 8px",
                                        textAlign: "right",
                                        color: agreementColor(row.agreement),
                                      }}
                                    >
                                      {noActual
                                        ? "–"
                                        : formatDelta(row.signedError)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div
                              className="muted"
                              style={{ fontSize: "10px", marginTop: 2 }}
                            >
                              {baselineKpis === null
                                ? "Baseline KPIs are not in the run rail yet, "
                                  + "so per-KPI actual Δ cannot be computed; "
                                  + "predicted Δ shown for reference."
                                : "Actual Δ = member run KPI minus session "
                                  + "baseline KPI (run-rail kpi_scores). "
                                  + "Predicted Δ is logged only, never scored."}
                            </div>
                          </div>
                        )}
                      </details>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* Event log (collapsible) — surfaces driver/Claude lifecycle
              events. Useful for any session but hidden by default to
              keep the panel light. */}
          {events.length > 0 && (
            <details
              data-testid="auto-research-event-log"
              style={{ marginTop: 8, fontSize: "11px" }}
            >
              <summary className="muted">
                event log ({events.length})
              </summary>
              <ul
                style={{
                  margin: "4px 0 0", paddingLeft: 16,
                  maxHeight: 180, overflowY: "auto",
                }}
              >
                {events.slice(-50).map((ev) => {
                  // Per-kind summary line derived from the payload (never
                  // throws, falls back to null). Recognised kinds show the
                  // summary inline; anything unrecognised with a non-empty
                  // payload gets an expandable raw-JSON disclosure so the
                  // content is never silently dropped.
                  const summary = summarizeAutoResearchEvent(ev);
                  const payloadKeys =
                    ev.payload && typeof ev.payload === "object"
                      ? Object.keys(ev.payload)
                      : [];
                  return (
                    <li key={ev.id} data-testid={`event-${ev.id}`}>
                      <code>{ev.kind}</code>{" "}
                      <span className="muted">{ev.ts}</span>
                      {summary && (
                        <span data-testid={`event-summary-${ev.id}`}>
                          {" · "}
                          {summary}
                        </span>
                      )}
                      {!summary && payloadKeys.length > 0 && (
                        <details
                          data-testid={`event-json-${ev.id}`}
                          style={{ marginLeft: 12 }}
                        >
                          <summary
                            className="muted"
                            style={{ cursor: "pointer" }}
                          >
                            payload
                          </summary>
                          <pre
                            style={{
                              margin: "2px 0 0",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontSize: "10px",
                            }}
                          >
                            {safeStringifyPayload(ev.payload)}
                          </pre>
                        </details>
                      )}
                    </li>
                  );
                })}
              </ul>
            </details>
          )}
        </div>
      )}

      {error && (
        <p
          data-testid="auto-research-error"
          style={{ color: "#ff9a9a", fontSize: "12px", marginTop: "8px" }}
        >
          {error}
        </p>
      )}
    </div>
  );
}
